import { Component, inject, ChangeDetectionStrategy, signal, computed } from '@angular/core';
import {CurrencyPipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MatDialogRef} from '@angular/material/dialog';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatTooltip} from '@angular/material/tooltip';
import {Entity} from '../../entity/entity';
import {StockUnit} from '../../products/stock-unit';
import {StockQuoteRequest} from '../../products/stock-quote-request';
import {StockUnitService} from '../../services/stock-unit.service';
import {PersonLoginService} from '../../services/person-login-service';
import {OrganizationProjectProductTypeService} from '../../services/organization-projectproducttype-service';
import {OrgunitXProjectProductType} from '../../organization/organization-brands/orgunit-x-projectproducttype';
import {StringUtils} from '../../utils/string-utils';
import {BrandID} from '../../enums/brand-id';
import {SerialNumberResearchService} from '../../services/serial-number-research.service';
import {DecoderData} from '../../model/decoder-data';
import {DecoderTableComponent} from '../../decoder/decoder-table.component';

export type StockProgram = 'STK' | 'HOT';
export type StockTypeFilter = 'RES' | 'COM' | 'PRT';
export type StockSortColumn = 'serialNumber' | 'modelNumber' | 'description' | 'productType' | 'unitPrice' | 'netPrice';
export type DecoderState = 'loading' | 'loaded' | 'empty' | 'error';
export type FaqDocument = { type: StockTypeFilter, label: string, fileName: string };

@Component({
    selector: 'stock-units-dialog',
    templateUrl: 'stock-units-dialog.component.html',
    styleUrls: ['./stock-units-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, CurrencyPipe, MatButton, MatIconButton, MatIcon, MatProgressSpinner, MatTooltip, DecoderTableComponent]
})
export class StockUnitsDialogComponent {
  private readonly stockUnitService = inject(StockUnitService);
  private readonly serialNumberResearchService = inject(SerialNumberResearchService);
  private readonly personLoginService = inject(PersonLoginService);
  private readonly organizationProjectProductTypeService = inject(OrganizationProjectProductTypeService);
  private readonly dialogRef = inject<MatDialogRef<StockUnitsDialogComponent>>(MatDialogRef);

  static readonly QUICK_STOCK: StockProgram = 'STK';
  static readonly QUICK_DEAL: StockProgram = 'HOT';
  static readonly BANNER_TEXT = "First come, first served - adding a unit to your quote does not hold it, and it isn't secured until your order is approved.";
  static readonly MIXING_MESSAGE = "Residential and Commercial units can't be on the same quote";
  static readonly PRICE_NOTE_TEXT = 'List price and your net price; the quote applies the same multiplier.';
  static readonly QUICK_DEAL_PRICE_NOTE_TEXT = "Quick Deal prices are already discounted for the unit's age; the quote uses this price as is.";
  static readonly NO_DECODER_TEXT = 'No model details are available for this part number.';
  static readonly FAQ_DOCUMENT_PATH = 'assets/documents/';
  static readonly FAQ_DOCUMENTS: FaqDocument[] = [
    {type: 'COM', label: 'Quick Programs Customer FAQ – Commercial', fileName: 'Quick-Programs-Customer-FAQ-Commercial.pdf'},
    {type: 'RES', label: 'Quick Programs Customer FAQ – Residential', fileName: 'Quick-Programs-Customer-FAQ-Residential.pdf'},
  ];
  static readonly DECODER_ERROR_TEXT = 'Model details could not be loaded.';

  readonly brandID: BrandID = BrandID.CLM;
  readonly bannerText = StockUnitsDialogComponent.BANNER_TEXT;
  readonly mixingMessage = StockUnitsDialogComponent.MIXING_MESSAGE;
  readonly noDecoderText = StockUnitsDialogComponent.NO_DECODER_TEXT;
  static readonly ALL_TYPE_FILTERS: { code: StockTypeFilter, label: string }[] = [
    {code: 'RES', label: 'Residential'},
    {code: 'COM', label: 'Commercial'},
  ];

  readonly officeTypes = signal<StockTypeFilter[] | null>(null);

  readonly typeFilters = computed<{ code: StockTypeFilter, label: string }[]>(() => {
    const office = this.officeTypes();
    if (!office || office.length === 0) {
      return StockUnitsDialogComponent.ALL_TYPE_FILTERS;
    }
    return StockUnitsDialogComponent.ALL_TYPE_FILTERS.filter(filter => office.includes(filter.code));
  });

  readonly showTypeSelector = computed(() => {
    const office = this.officeTypes();
    return !office || office.length !== 1;
  });

  readonly faqDocuments = computed<FaqDocument[]>(() => {
    const office = this.officeTypes();
    if (!office || office.length !== 1) {
      return StockUnitsDialogComponent.FAQ_DOCUMENTS;
    }
    return StockUnitsDialogComponent.FAQ_DOCUMENTS.filter(doc => doc.type === office[0]);
  });

  readonly program = signal<StockProgram>(StockUnitsDialogComponent.QUICK_STOCK);
  readonly infoDismissed = signal(false);
  readonly units = signal<StockUnit[]>([]);
  readonly isLoading = signal(false);
  readonly isCreating = signal(false);
  readonly errorMessage = signal<string>(undefined!);
  readonly typeFilter = signal<StockTypeFilter>('RES');
  readonly searchTerm = signal('');
  readonly sortColumn = signal<StockSortColumn>('modelNumber');
  readonly sortAscending = signal(true);
  readonly selectedGuids = signal<string[]>([]);
  readonly expandedGuid = signal<string | null>(null);
  readonly decoderDataByGuid = signal<Record<string, DecoderData>>({});
  readonly decoderStateByGuid = signal<Record<string, DecoderState>>({});
  readonly decoderError = signal<string | null>(null);

  readonly isBusy = computed(() => this.isLoading() || this.isCreating());

  readonly isQuickStock = computed(() => this.program() === StockUnitsDialogComponent.QUICK_STOCK);

  readonly programLabel = computed(() => this.isQuickStock() ? 'Quick Stock' : 'Quick Deal');

  readonly priceNoteText = computed(() => this.isQuickStock()
    ? StockUnitsDialogComponent.PRICE_NOTE_TEXT
    : StockUnitsDialogComponent.QUICK_DEAL_PRICE_NOTE_TEXT);

  readonly priceColumnLabel = computed(() => this.isQuickStock() ? 'List Price' : 'Price');

  readonly subtotalLabel = computed(() => this.isQuickStock() ? 'List subtotal' : 'Subtotal');

  readonly filtered = computed<StockUnit[]>(() => {
    const term = this.searchTerm().trim().toUpperCase();
    const type = this.typeFilter();
    const column = this.sortColumn();
    const direction = this.sortAscending() ? 1 : -1;
    return this.units()
      .filter(unit => this.normalizeType(unit.productType) === type || this.normalizeType(unit.productType) === 'PRT')
      .filter(unit => term.length === 0 || this.matchesSearch(unit, term))
      .sort((a, b) => direction * this.compare(a, b, column));
  });

  readonly selectedUnits = computed<StockUnit[]>(() => {
    const selected = this.selectedGuids();
    return this.units().filter(unit => selected.includes(unit.productGuid));
  });

  readonly selectedCount = computed(() => this.selectedUnits().length);

  readonly subtotal = computed(() => this.selectedUnits().reduce((sum, unit) => sum + (unit.unitPrice || 0), 0));
  readonly hasNetPrices = computed(() => this.units().some(unit => unit.netPrice != null));
  readonly showNetPrice = computed(() => this.program() === StockUnitsDialogComponent.QUICK_STOCK && this.hasNetPrices());
  readonly netSubtotal = computed(() => this.selectedUnits().reduce((sum, unit) => sum + (unit.netPrice || 0), 0));

  readonly lockedType = computed<StockTypeFilter | null>(() => {
    const types = this.selectedUnits().map(unit => this.normalizeType(unit.productType));
    if (types.includes('RES')) {
      return 'COM';
    }
    if (types.includes('COM')) {
      return 'RES';
    }
    return null;
  });

  readonly selectableVisible = computed(() => this.filtered().filter(unit => !this.isLocked(unit)));
  readonly allVisibleSelected = computed(() => {
    const selectable = this.selectableVisible();
    return selectable.length > 0 && selectable.every(unit => this.isSelected(unit));
  });
  readonly someVisibleSelected = computed(() => !this.allVisibleSelected() && this.filtered().some(unit => this.isSelected(unit)));

  constructor() {
    this.loadOfficeTypes();
    this.loadUnits();
  }

  private loadOfficeTypes(): void {
    const personLogin = this.personLoginService.getCachedLoginWithoutRedirect();
    const orgUnitId = personLogin?.person?.primaryOrgUnitId;
    if (!orgUnitId || this.personLoginService.isAdmin()) {
      return;
    }
    this.organizationProjectProductTypeService.getByOrgUnitId(orgUnitId).subscribe({
      next: (results: OrgunitXProjectProductType[]) => {
        const types = (results || [])
          .filter(result => result.brandID === this.brandID)
          .map(result => this.normalizeType(result.projectProductType))
          .filter(type => type !== 'PRT');
        const officeTypes = [...new Set(types)];
        this.officeTypes.set(officeTypes);
        this.typeFilter.set(officeTypes.length > 0 ? officeTypes[0] : 'RES');
      },
      error: () => this.officeTypes.set(null)
    });
  }

  private loadUnits(): void {
    this.isLoading.set(true);
    this.errorMessage.set(undefined!);
    this.stockUnitService.getStockUnits(this.program(), this.brandID).subscribe({
      next: (results: StockUnit[]) => {
        this.units.set((results || []).filter(unit => unit.active !== false));
        this.isLoading.set(false);
      },
      error: (error: any) => this.handleError(error)
    });
  }

  dismissInfo(): void {
    this.infoDismissed.set(true);
  }

  faqUrl(doc: FaqDocument): string {
    return StockUnitsDialogComponent.FAQ_DOCUMENT_PATH + doc.fileName;
  }

  private handleError(error: any): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isLoading.set(false);
    this.isCreating.set(false);
  }

  private matchesSearch(unit: StockUnit, term: string): boolean {
    return [unit.serialNumber, unit.modelNumber, unit.description]
      .some(value => value != null && value.toUpperCase().includes(term));
  }

  private compare(a: StockUnit, b: StockUnit, column: StockSortColumn): number {
    if (column === 'unitPrice' || column === 'netPrice') {
      return (a[column] || 0) - (b[column] || 0);
    }
    const left = column === 'productType' ? this.typeLabel(a.productType) : (a[column] || '');
    const right = column === 'productType' ? this.typeLabel(b.productType) : (b[column] || '');
    return left.localeCompare(right, undefined, {numeric: true, sensitivity: 'base'});
  }

  normalizeType(productType: string): StockTypeFilter {
    switch ((productType || '').toUpperCase()) {
      case 'RES':
        return 'RES';
      case 'COM':
        return 'COM';
      default:
        return 'PRT';
    }
  }

  typeLabel(productType: string): string {
    switch (this.normalizeType(productType)) {
      case 'RES':
        return 'Residential';
      case 'COM':
        return 'Commercial';
      default:
        return 'Part';
    }
  }

  typeBadgeClass(productType: string): string {
    switch (this.normalizeType(productType)) {
      case 'RES':
        return 'badgeResidential';
      case 'COM':
        return 'badgeCommercial';
      default:
        return 'badgePart';
    }
  }

  isSelected(unit: StockUnit): boolean {
    return this.selectedGuids().includes(unit.productGuid);
  }

  isLocked(unit: StockUnit): boolean {
    const locked = this.lockedType();
    return locked !== null && this.normalizeType(unit.productType) === locked && !this.isSelected(unit);
  }

  selectProgram(program: StockProgram): void {
    if (program === this.program() || this.isBusy()) {
      return;
    }
    this.program.set(program);
    this.selectedGuids.set([]);
    this.searchTerm.set('');
    this.expandedGuid.set(null);
    this.errorMessage.set(undefined!);
    if (this.sortColumn() === 'netPrice') {
      this.sortColumn.set('modelNumber');
      this.sortAscending.set(true);
    }
    this.loadUnits();
  }

  selectTypeFilter(filter: StockTypeFilter): void {
    if (this.isTypeFilterLocked(filter)) {
      return;
    }
    this.typeFilter.set(filter);
  }

  isTypeFilterLocked(filter: StockTypeFilter): boolean {
    return this.lockedType() === filter;
  }

  typeFilterTooltip(filter: StockTypeFilter): string {
    if (!this.isTypeFilterLocked(filter)) {
      return '';
    }
    const selected = filter === 'RES' ? 'Commercial' : 'Residential';
    return `Only one type can be on a quote. Clear the selected ${selected} units to switch.`;
  }

  sortBy(column: StockSortColumn): void {
    if (this.sortColumn() === column) {
      this.sortAscending.update(current => !current);
    } else {
      this.sortColumn.set(column);
      this.sortAscending.set(true);
    }
  }

  toggleUnit(unit: StockUnit): void {
    if (this.isBusy() || this.isLocked(unit)) {
      return;
    }
    this.errorMessage.set(undefined!);
    this.selectedGuids.update(current => this.isSelected(unit)
      ? current.filter(guid => guid !== unit.productGuid)
      : [...current, unit.productGuid]);
  }

  isExpanded(unit: StockUnit): boolean {
    return this.expandedGuid() === unit.productGuid;
  }

  decoderFor(unit: StockUnit): DecoderData | undefined {
    return this.decoderDataByGuid()[unit.productGuid];
  }

  decoderState(unit: StockUnit): DecoderState {
    return this.decoderStateByGuid()[unit.productGuid] || 'loading';
  }

  toggleDecoder(event: Event, unit: StockUnit): void {
    event.stopPropagation();
    if (this.isExpanded(unit)) {
      this.expandedGuid.set(null);
      return;
    }
    this.expandedGuid.set(unit.productGuid);
    const state = this.decoderStateByGuid()[unit.productGuid];
    if (state === 'loaded' || state === 'empty') {
      return;
    }
    this.decoderError.set(null);
    this.setDecoderState(unit, 'loading');
    this.serialNumberResearchService.getDecoderData(unit.modelNumber).subscribe({
      next: (data: DecoderData) => {
        if (data) {
          this.decoderDataByGuid.update(current => ({...current, [unit.productGuid]: data}));
          this.setDecoderState(unit, 'loaded');
        } else {
          this.setDecoderState(unit, 'empty');
        }
      },
      error: (error: any) => {
        this.decoderError.set(StringUtils.getErrorMessage(error) || StockUnitsDialogComponent.DECODER_ERROR_TEXT);
        this.setDecoderState(unit, 'error');
      },
    });
  }

  private setDecoderState(unit: StockUnit, state: DecoderState): void {
    this.decoderStateByGuid.update(current => ({...current, [unit.productGuid]: state}));
  }

  toggleAllVisible(): void {
    if (this.isBusy()) {
      return;
    }
    if (this.allVisibleSelected()) {
      const visible = this.filtered().map(unit => unit.productGuid);
      this.selectedGuids.update(current => current.filter(guid => !visible.includes(guid)));
      return;
    }
    for (const unit of this.filtered()) {
      if (!this.isSelected(unit) && !this.isLocked(unit)) {
        this.selectedGuids.update(current => [...current, unit.productGuid]);
      }
    }
  }

  createQuote(): void {
    const selected = this.selectedUnits();
    if (selected.length === 0 || this.isBusy()) {
      return;
    }
    const types = selected.map(unit => this.normalizeType(unit.productType));
    if (types.includes('RES') && types.includes('COM')) {
      this.errorMessage.set(StockUnitsDialogComponent.MIXING_MESSAGE);
      return;
    }
    this.isCreating.set(true);
    this.errorMessage.set(undefined!);
    const request = new StockQuoteRequest();
    request.brandID = this.brandID;
    request.serialNumbers = selected.map(unit => unit.serialNumber);
    this.stockUnitService.createStockQuote(request).subscribe({
      next: (entity: Entity) => {
        this.isCreating.set(false);
        this.dialogRef.close(entity);
      },
      error: (error: any) => this.handleError(error)
    });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

}
